/** App-wide session: the API client, who I am, and which business and location I'm looking at. */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { withCardPrompts } from "./lib/cardPrompt.ts";
import { withDemoProgress } from "./lib/demoAsk.ts";
import { withDemoOnly } from "./lib/demoOnly.ts";
import { withDemoLock } from "./lib/demoTour.ts";
import { canDo, ensureStatesLoaded, stateName, type Organization, type PlanAccess, type Role } from "@logwave/core";
import { ApiError, PLAN_REFUSED_EVENT, createHttpApi, type Api, type LocationX, type Me } from "./api.ts";
import { useAuth } from "./auth.tsx";
import { setVoiceTranslator } from "./lib/speechLang.ts";
import { config, IS_MOCK } from "./env.ts";
import { AUTH_MESSAGES } from "./lib/authClient.ts";
import { ROLE_RANK } from "./lib/labels.ts";
import { createMockApi } from "./mock/api.ts";
import { sampleOfficialWanted } from "./mock/official.ts";
import { sharedMockStore } from "./mock/store.ts";
import { withoutPurchases } from "./lib/storeApp.ts";

/* ─────────────── API client ─────────────── */

const ApiContext = createContext<Api | null>(null);

/** How live sync reaches the data: the API's shape proxy, or (mock mode) the store's cross-tab change signal. */
export interface SyncTarget {
  shapeBase(orgId: string): string | null;
  headers(): Promise<Record<string, string>>;
  subscribeLocal?: (fn: () => void) => () => void;
}
const SyncContext = createContext<SyncTarget>({ shapeBase: () => null, headers: async () => ({}) });

export function useApi(): Api {
  const a = useContext(ApiContext);
  if (!a) throw new Error("useApi outside ApiProvider");
  return a;
}
export const useSyncTarget = () => useContext(SyncContext);
/** The API client, or null outside an ApiProvider (the error card on the sign-in screen). */
export const useApiMaybe = (): Api | null => useContext(ApiContext);

export const MOCK_DEMO_KEY = "logwave.mock.demo";
export function mockDemoWanted(): boolean {
  try { return localStorage.getItem(MOCK_DEMO_KEY) !== "0"; } catch { return true; }
}

/** Demo sessions: a revoked or expired token answers 401 on the next call, as the API does (docs/AUTH.md). */
function guardSession(api: Api, valid: () => boolean, expire: () => void): Api {
  const out: Record<string, unknown> = {};
  for (const [k, fn] of Object.entries(api) as [string, (...a: unknown[]) => unknown][]) {
    out[k] = (...a: unknown[]) => {
      if (!valid()) { expire(); return Promise.reject(new ApiError(401, "unauthenticated", AUTH_MESSAGES.signed_out)); }
      return fn(...a);
    };
  }
  return out as unknown as Api;
}

/** Builds the client for the signed-in user: HTTP against the Bun API, or the in-browser mock. Tests pass `api` directly. */
export function ApiProvider({ api: given, children }: { api?: Api; children: ReactNode }) {
  const auth = useAuth();
  const email = auth.user?.email;
  const api = useMemo<Api | null>(() => {
    if (given) return given;
    if (IS_MOCK) {
      if (!email) return null;
      const mock = createMockApi({ store: sharedMockStore(), email, name: auth.user?.name, demo: mockDemoWanted(), latencyMs: 25, official: sampleOfficialWanted() });
      // The card prompt at the value moment (lib/cardPrompt.ts): raised by saves, never by check-ins or reports.
      // The demo build (config.demo): photos and AI show the two choices instead of running, and Ask answers count toward the view cap.
      return withCardPrompts(withDemoLock(withDemoProgress(auth.kind === "session" ? guardSession(withDemoOnly(mock), auth.sessionValid, auth.expire) : withDemoOnly(mock))));
    }
    // Opened from the phone app (?via=app): no purchase call goes out (lib/storeApp.ts, App Store 3.1.3(f), Play payments).
    return withCardPrompts(withoutPurchases(createHttpApi({ baseUrl: config.apiUrl, auth: auth.headers, onUnauthorized: auth.expire })));
  }, [given, email, auth.headers, auth.user?.name, auth.kind, auth.sessionValid, auth.expire]);
  const sync = useMemo<SyncTarget>(() => {
    if (given) return { shapeBase: () => null, headers: auth.headers };
    if (IS_MOCK) return { shapeBase: () => null, headers: auth.headers, subscribeLocal: (fn) => sharedMockStore().subscribe(fn) };
    return { shapeBase: (orgId) => `${config.apiUrl}/orgs/${encodeURIComponent(orgId)}/shape`, headers: auth.headers };
  }, [given, auth.headers]);
  if (!api) return <>{children}</>;
  return <ApiContext.Provider value={api}><SyncContext.Provider value={sync}>{children}</SyncContext.Provider></ApiContext.Provider>;
}

/* ─────────────── session (me, org, location) ─────────────── */

/** A business or a home you belong to (docs/WORKSPACES.md), with your role there. */
export interface Workspace { org: Organization; role: Role; home: boolean }

/** Every business, then every home, once each. The API lists homes apart; the demo also lists them in memberships. */
export function workspacesOf(me: Me): Workspace[] {
  const seen = new Set<string>();
  const out: Workspace[] = [];
  for (const m of [...me.memberships, ...(me.homes ?? [])]) {
    if (!m.org || seen.has(m.org.id)) continue;
    seen.add(m.org.id);
    out.push({ org: m.org, role: m.role, home: m.org.kind === "home" });
  }
  return [...out.filter((w) => !w.home), ...out.filter((w) => w.home)];
}

/** A home has no business location on the API; its screens hang off this stand-in. */
function homePlace(org: Organization): LocationX {
  return { id: org.id, orgId: org.id, name: org.name, address: "", county: "", permitType: "private_members_only", timezone: "America/Denver", areas: [], openAt: "00:00", closeAt: "23:59", menuItemIds: [], stage: 1, createdAt: org.createdAt };
}

export interface Session {
  me: Me;
  /** Businesses and homes you can switch to. */
  workspaces: Workspace[];
  org: Organization;
  role: Role;
  employeeId?: string;
  /** The plan gate (core paywall.ts) for this business; undefined = an older server or the demo (everything works). */
  plan?: PlanAccess;
  /** Who staff ask ("Ask Rae to restart the plan"). */
  ownerName?: string | null;
  locations: LocationX[];
  location: LocationX;
  can(min: Role): boolean;
  /**
   * What the owner shares with this person here (core ownership.ts: Billing, People, Settings). Owners always; staff never. The
   * server refuses what's off; the app hides it.
   */
  may(sw: import("@logwave/core").Switch): boolean;
  /** Sees the plan's cost: Billing on, or added the card on file, or a location's billing admin (the server sends billing then). */
  seesCost: boolean;
  selectOrg(orgId: string): void;
  selectLocation(id: string): void;
  /** Open one place of one of the person's businesses (a "Fix and log it now" link to a place they run): that business, that place, for now only (run-27 R27-02: a link alone never changes the saved default; `keepPlace` does, on the first logged step). */
  selectPlace(orgId: string, locationId: string): void;
  /** Make the workspace and place now open the person's default (what selectPlace leaves alone until a real act: run-27 R27-02). */
  keepPlace(): void;
  refresh(): Promise<void>;
  /** Replace one location in memory after an edit. */
  patchLocation(loc: LocationX): void;
}

interface SessionState {
  loading: boolean;
  error?: string;
  /** "states": the person's rules could not be fetched (not a network failure of the API): the retry says so. */
  errorKind?: "states";
  /** The workspace the failed load was for, so Try again asks for the same one. */
  retryOrg?: string;
  me?: Me;
  locations: LocationX[];
  orgId?: string;
  locationId?: string;
}

/** `reload(orgId)` opens that workspace (after setting up a new business or home); with no id, the current one. */
const SessionContext = createContext<(Partial<Session> & { state: SessionState; reload(orgId?: string): Promise<void> }) | null>(null);

const pref = {
  get(k: string) { try { return localStorage.getItem(k) ?? undefined; } catch { return undefined; } },
  set(k: string, v: string) { try { localStorage.setItem(k, v); } catch { /* ignore */ } },
};

export function SessionProvider({ children }: { children: ReactNode }) {
  const api = useApi();
  const [state, setState] = useState<SessionState>({ loading: true, locations: [] });

  const load = useCallback(async (orgWanted?: string, only?: { locationId: string }) => {
    setState((s) => ({ ...s, loading: true, error: undefined, errorKind: undefined, retryOrg: undefined }));
    try {
      const me = await api.me();
      // Signed in: the public library skips its free-guide meter for members (apps/landing/library-src/meter.js).
      try { document.cookie = "cc_member=1; max-age=31536000; path=/; samesite=lax"; } catch { /* no cookies */ }
      const all = workspacesOf(me);
      // A link that names the business (?org=…: "Walk the rooms" opened from the team site, or a place just made yours) opens it, when it's yours.
      const fromLink = (() => { try { return new URLSearchParams(window.location.search).get("org") ?? undefined; } catch { return undefined; } })();
      const orgId = [orgWanted, fromLink, pref.get("logwave.org")].find((id) => id && all.some((w) => w.org.id === id)) ?? all[0]?.org.id;
      const ws = all.find((w) => w.org.id === orgId);
      // A home answers business routes with not_found on the API; the demo gives it a location row.
      const locations = !orgId ? [] : ws?.home ? await api.locations(orgId).then((l) => (l.length ? l : [homePlace(ws.org)]), () => [homePlace(ws.org)]) : await api.locations(orgId);
      // A home's own products live in its profile (routes/products.ts): put them on the stand-in, so every buy link follows them.
      if (ws?.home && orgId && locations[0] && !locations[0].productOverrides) {
        const own = await Promise.resolve().then(() => api.homeProducts(orgId)).then((r) => r?.overrides ?? [], () => []);
        if (own.length) locations[0] = { ...locations[0], productOverrides: own };
      }
      const m = me.memberships.find((x) => x.org.id === orgId);
      const allowed = locations.filter((l) => !m || m.locationIds.length === 0 || m.locationIds.includes(l.id));
      const saved = only?.locationId ?? pref.get(`logwave.loc.${orgId}`);
      const locationId = allowed.find((l) => l.id === saved)?.id ?? allowed[0]?.id;
      // Each state's rules load on demand (core states/loaders.ts): this workspace's state and each of its places' states must be
      // readable before any screen asks for them. The screen that is open stays as it was until they are.
      const needed = [ws?.org.state, ...allowed.map((l) => l.state)];
      try { await ensureStatesLoaded(needed); } catch {
        const names = [...new Set(needed.filter((x): x is NonNullable<typeof x> => !!x))].map((x) => stateName(x));
        setState((s) => ({ ...s, loading: false, errorKind: "states", retryOrg: orgId, error: `We couldn't load the ${names.join(" and ")} rules. Check your connection, then try again.` }));
        return;
      }
      setState({ loading: false, me, orgId, locations: allowed, locationId });
    } catch (e) {
      setState((s) => ({ ...s, loading: false, errorKind: undefined, error: e instanceof Error ? e.message : String(e) }));
    }
  }, [api]);

  useEffect(() => { void load(); }, [load]);
  // A plan-gate refusal (api.ts PLAN_REFUSED_EVENT): re-read /me once, so the lock screen or the plan card replaces the refused screen.
  const orgRef = useRef(state.orgId);
  orgRef.current = state.orgId;
  useEffect(() => {
    let last = 0;
    const on = () => { const t = Date.now(); if (t - last < 3000) return; last = t; void load(orgRef.current); };
    try { window.addEventListener(PLAN_REFUSED_EVENT, on); } catch { return; }
    return () => window.removeEventListener(PLAN_REFUSED_EVENT, on);
  }, [load]);
  // Voice notes in other languages go through our server translation (lib/speechLang.ts).
  useEffect(() => {
    const orgId = state.orgId;
    setVoiceTranslator(orgId ? (text, lang) => api.voiceTranslate(orgId, { text, lang }).then((r) => r.english) : undefined);
    return () => setVoiceTranslator(undefined);
  }, [api, state.orgId]);

  const value = useMemo(() => {
    const { me, orgId, locationId, locations } = state;
    const all = me ? workspacesOf(me) : [];
    const ws = all.find((x) => x.org.id === orgId);
    const mem = me?.memberships.find((x) => x.org.id === orgId);
    const homeMem = me?.homes?.find((x) => x.org.id === orgId);
    const m = ws ? { org: ws.org, role: ws.role, employeeId: mem?.employeeId } : undefined;
    const location = locations.find((l) => l.id === locationId);
    const base = { state, reload: (id?: string) => { if (id) pref.set("logwave.org", id); return load(id ?? orgId); } };
    if (!me || !m || !location) return { ...base, me };
    const session: Session = {
      me, workspaces: all, org: m.org, role: m.role, employeeId: m.employeeId, locations, location, plan: (mem?.plan ?? homeMem?.plan) ?? undefined, ownerName: mem?.ownerName ?? homeMem?.ownerName,
      // Setting a place up for its owner: the walk and prep screens are theirs (the server lets only those through, setupgate.ts), so they read as an admin here.
      can: (min) => ROLE_RANK[m.role === "setup" ? "admin" : m.role] >= ROLE_RANK[min],
      may: (sw) => canDo({ role: m.role, ...(mem?.switches ? { permissions: mem.switches } : {}) }, sw),
      seesCost: canDo({ role: m.role, ...(mem?.switches ? { permissions: mem.switches } : {}) }, "billing") || (m.role !== "staff" && !!m.org.billing),
      selectOrg: (id) => { pref.set("logwave.org", id); void load(id); },
      selectLocation: (id) => { pref.set(`logwave.loc.${orgId}`, id); setState((s) => ({ ...s, locationId: id })); },
      selectPlace: (org, loc) => {
        if (org === orgId) { setState((st) => ({ ...st, locationId: loc })); return; }
        void load(org, { locationId: loc }); // transient: nothing saved (run-27 R27-02)
      },
      keepPlace: () => { if (orgId) pref.set("logwave.org", orgId); if (orgId && locationId) pref.set(`logwave.loc.${orgId}`, locationId); },
      refresh: () => load(orgId),
      patchLocation: (loc) => setState((s) => ({ ...s, locations: s.locations.map((l) => (l.id === loc.id ? loc : l)) })),
    };
    return { ...base, ...session };
  }, [state, load]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

/** The session if one is ready, else null; never throws (error card, forms that also render standalone). */
export function useSessionMaybe(): Session | null {
  const s = useContext(SessionContext);
  return s && s.org && s.location && s.me && s.can ? (s as Session) : null;
}

/** Raw session state, for the gate that decides between loading, setup and the app. */
export function useSessionState() {
  const s = useContext(SessionContext);
  if (!s) throw new Error("useSessionState outside SessionProvider");
  return s;
}

/** The ready session. Only use inside screens rendered after setup is complete. */
export function useSession(): Session {
  const s = useSessionState();
  if (!s.org || !s.location || !s.me || !s.can) throw new Error("Session not ready");
  return s as Session & typeof s;
}
